import { DecimalSchema } from "../../decimal";
import { z } from "zod";

// Spinning wheel result schema (API response DTO)
export const SpinningWheelResultSchema = z.object({
  id: z.uuid(),
  rewardAmount: DecimalSchema,
  rewardSymbol: z.string(),
  spinningWheelTicketId: z.uuid(),
  spinningWheelId: z.string(),
  epochId: z.number().int().positive().nullable(),
  createdAt: z.coerce.date(),
  cooldownEnd: z.coerce.date().optional(),
  claimedAt: z.coerce.date().optional(),
});

export type SpinningWheelResult = z.infer<typeof SpinningWheelResultSchema>;

// Request body for spin endpoint
export const SpinRequestBodySchema = z.object({
  ticketCount: z.number().int().positive(),
});

export type SpinRequestBody = z.infer<typeof SpinRequestBodySchema>;

// Response for list rewards endpoint
export const ListRewardsResponseSchema = z.object({
  rewards: z.array(
    z.object({
      rewardAmount: DecimalSchema,
      rewardSymbol: z.string(),
    }),
  ),
});

export type ListRewardsResponse = z.infer<typeof ListRewardsResponseSchema>;

// Response for get user spin results endpoint
export const GetUserSpinResultsResponseSchema = z.object({
  results: z.array(SpinningWheelResultSchema),
});

export type GetUserSpinResultsResponse = z.infer<typeof GetUserSpinResultsResponseSchema>;

// Response for get user tickets endpoint
export const GetUserTicketsResponseSchema = z.object({
  totalTickets: z.number().int().nonnegative(),
});

export type GetUserTicketsResponse = z.infer<typeof GetUserTicketsResponseSchema>;

// Response for threshold progress endpoint
export const GetThresholdProgressResponseSchema = z.object({
  currentUsdValue: DecimalSchema,
  thresholdUsdValue: DecimalSchema,
  isThresholdReached: z.boolean(),
  progressPercentage: z.number().min(0).max(100),
  pendingResultsCount: z.number().int().nonnegative(),
});

export type GetThresholdProgressResponse = z.infer<typeof GetThresholdProgressResponseSchema>;

// Query parameters for SSE endpoint
export const SpinResultSseSchema = z.object({
  userId: z.coerce.number().int().positive(),
});

export type SpinResultSseParams = z.infer<typeof SpinResultSseSchema>;

// Request body for admin grant tickets endpoint
export const GrantTicketsRequestSchema = z.object({
  hubUserId: z.number().int().positive(),
  ticketCount: z.number().int().positive().min(1).max(100),
});

export type GrantTicketsRequest = z.infer<typeof GrantTicketsRequestSchema>;

// In-proc DTOs for rewards distribution
export const SpinningWheelFinishedEpochSchema = z.object({
  epochId: z.number().int().positive(),
});

export type SpinningWheelFinishedEpoch = z.infer<typeof SpinningWheelFinishedEpochSchema>;

export const SpinningWheelEligibleUserSchema = z.object({
  defiAddress: z.string(),
  rewardAmount: z.string(), // Decimal as string for precision
  rewardSymbol: z.string(), // "USDT" | "WETH"
});

export type SpinningWheelEligibleUser = z.infer<typeof SpinningWheelEligibleUserSchema>;

export const GetUserSpinResultsQueryDtoSchema = z.object({
  defiAddress: z
    .string()
    .min(1)
    .transform((addr) => {
      // Normalize EVM addresses to lowercase, keep TON addresses as-is
      return addr.startsWith("0x") ? addr.toLowerCase() : addr;
    })
    .optional(),
});
export type GetUserSpinResultsQueryDto = z.infer<typeof GetUserSpinResultsQueryDtoSchema>;

// Response for recent winners endpoint
// Minimal information for privacy - no timestamps exposed
// Returns direct array of winners (no wrapper object)
export const GetRecentWinnersResponseSchema = z.array(
  z.object({
    defiAddress: z.string(), // Pre-truncated with formatShortAddress
    rewardAmount: DecimalSchema,
    rewardSymbol: z.string(),
    // NO wonAt field - don't expose timing information for privacy
  }),
);

export type GetRecentWinnersResponse = z.infer<typeof GetRecentWinnersResponseSchema>;

// Admin: Add winner request
export const AddWinnerRequestSchema = z.object({
  defiAddress: z.string().min(1), // Pre-truncated address like "0x1234...5678"
  rewardAmount: z.string(), // Decimal as string for precision
  rewardSymbol: z.string().min(1),
  wonAt: z.iso
    .datetime()
    .or(z.iso.date())
    .transform((val) => new Date(val)),
});

export type AddWinnerRequest = z.infer<typeof AddWinnerRequestSchema>;

// Admin: Add winner response
export const AddWinnerResponseSchema = z.object({
  id: z.uuid(),
});

export type AddWinnerResponse = z.infer<typeof AddWinnerResponseSchema>;

// Admin: Remove winner has no request body, just URL param (id)
